import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  server: {
    API_URL: z.string().min(1),
    YOUTUBE_API_URL: z.url().min(1),
    APP_URL: z.string().min(1),
    AUTH_SECRET: z.string().min(1),
    EXTENSION_URL: z.string().min(1),
    SITEMAP_API_KEY: z.string().min(1),
    POSTHOG_KEY: z.string().min(1),
    POSTHOG_HOST: z.string().min(1),
    POSTHOG_SERVICE_NAME: z.string().min(1),
    // Native deep-linking association secrets (see
    // docs/deep-linking-runbook.md). OPTIONAL on purpose: the build must not
    // block on them, and the `/.well-known` route handlers serve a clearly
    // inert placeholder until they are provided at deploy time.
    //
    // Apple Developer "Team ID" (10 chars) — used as `<TeamID>.com.skim.mobile`
    // in apple-app-site-association.
    APPLE_TEAM_ID: z.string().optional(),
    // Android app-signing SHA-256 cert fingerprint(s), comma-separated. Usually
    // TWO values are required: the Play upload key AND the Google Play
    // app-signing key (Play re-signs uploads).
    ANDROID_CERT_SHA256: z.string().optional(),
    // Shared secret for POST /api/revalidate, which the backend calls when an
    // analysis completes. OPTIONAL on purpose, and the route refuses every
    // request while it is unset: an unauthenticated revalidate endpoint is a
    // way for anyone to force unlimited rebuilds, which is precisely the cost
    // this is meant to remove. Must be at least 32 chars so a guess is not
    // cheaper than the rate limiter.
    REVALIDATE_SECRET: z.string().min(32).optional(),
    // Waitlist / event email capture (/AISalon). OPTIONAL so the build never
    // blocks on them; the capture action fails soft when they are absent.
    GOOGLE_CREDENTIALS: z.string().optional(),
    GOOGLE_SHEET_ID: z.string().optional(),
    GOOGLE_SHEET_NAME: z.string().optional(),
  },
  client: {
    NEXT_PUBLIC_API_URL: z.string().url().min(1),
    NEXT_PUBLIC_YOUTUBE_API_URL: z.url().min(1),
    NEXT_PUBLIC_YT_WEBSOCKET_URL: z.url().min(1),
    NEXT_PUBLIC_APP_URL: z.string().url().min(1),
    NEXT_PUBLIC_CWS_URL: z.string().min(1),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().min(1),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().min(1),
    NEXT_PUBLIC_POSTHOG_SERVICE_NAME: z.string().min(1),
    // Self-report survey arm switch (Wave-4 item 40 / CRE-858). DEFAULT OFF:
    // the survey mounts ONLY when this is exactly '1' AND the PostHog
    // feature flag `self_report_survey_enabled` is on (double gate). Arming
    // is Aki + Yan's explicit call - see self-report-survey.tsx.
    NEXT_PUBLIC_SELF_REPORT_SURVEY: z.enum(['0', '1']).default('0'),
    // First-party `UserEvent` recording via the backend `POST /events`.
    // DEFAULT ON since the backend replaced `assertStaffAccess` with a
    // self-access check (cred-backend-1.0 `4b42b49`): a user may now record
    // their own events, which was the one blocker. Ship '0' to disable without
    // a code change — the emitters short-circuit before any network call.
    NEXT_PUBLIC_USER_EVENTS_ENABLED: z.enum(['0', '1']).default('1'),
  },
  runtimeEnv: {
    API_URL: process.env.API_URL,
    YOUTUBE_API_URL: process.env.YOUTUBE_API_URL,
    APP_URL: process.env.APP_URL,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_YOUTUBE_API_URL: process.env.NEXT_PUBLIC_YOUTUBE_API_URL,
    NEXT_PUBLIC_YT_WEBSOCKET_URL: process.env.NEXT_PUBLIC_YT_WEBSOCKET_URL,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    AUTH_SECRET: process.env.AUTH_SECRET,
    NEXT_PUBLIC_CWS_URL: process.env.NEXT_PUBLIC_CWS_URL,
    EXTENSION_URL: process.env.EXTENSION_URL,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    SITEMAP_API_KEY: process.env.SITEMAP_API_KEY,
    POSTHOG_KEY: process.env.POSTHOG_KEY,
    POSTHOG_HOST: process.env.POSTHOG_HOST,
    POSTHOG_SERVICE_NAME: process.env.POSTHOG_SERVICE_NAME,
    APPLE_TEAM_ID: process.env.APPLE_TEAM_ID,
    ANDROID_CERT_SHA256: process.env.ANDROID_CERT_SHA256,
    REVALIDATE_SECRET: process.env.REVALIDATE_SECRET,
    GOOGLE_CREDENTIALS: process.env.GOOGLE_CREDENTIALS,
    GOOGLE_SHEET_ID: process.env.GOOGLE_SHEET_ID,
    GOOGLE_SHEET_NAME: process.env.GOOGLE_SHEET_NAME,
    NEXT_PUBLIC_POSTHOG_SERVICE_NAME:
      process.env.NEXT_PUBLIC_POSTHOG_SERVICE_NAME,
    NEXT_PUBLIC_SELF_REPORT_SURVEY: process.env.NEXT_PUBLIC_SELF_REPORT_SURVEY,
    NEXT_PUBLIC_USER_EVENTS_ENABLED:
      process.env.NEXT_PUBLIC_USER_EVENTS_ENABLED,
  },
});
